import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Maximize2, X } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, Customized } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ChartModal from './ChartModal';
import { useFilters } from '../contexts/FilterContext';
import { sparesAnalyticsAPI, equipmentAPI } from '../services/api';

// Helper to format dates for API in local time
const formatDateForApi = (date) => {
  if (!(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Spares = ({ activeTab = 'Spares' }) => {
  const { filters, manualTrigger, setAvailableMachines } = useFilters();

  // State management for maximize functionality
  const [maximizedChartId, setMaximizedChartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalEquipmentFilter, setModalEquipmentFilter] = useState('all');
  const [modalSortOrder, setModalSortOrder] = useState('low-high');
  const [sortOrder, setSortOrder] = useState({}); // Track sort order for each chart
  const [selectedVoucherType, setSelectedVoucherType] = useState(null); // Track selected voucher type for drill-down
  const [selectedItemName, setSelectedItemName] = useState(null); // Track selected item name for drill-down
  const [selectedSupplierName, setSelectedSupplierName] = useState(null); // Track selected supplier name for drill-down

  // State for spares data
  const [keyMetrics, setKeyMetrics] = useState({
    totalExpenditure: { value: 0, suffix: '', unit: 'Rupees' },
    uptimeOfSpares: { value: 0, unit: 'HMR' },
    avgAmtSpendOnSpares: { value: 0, unit: 'Rupees' },
    totalNoOfSparesOrdered: { value: 0, suffix: '', unit: '' },
    finalMetric: { value: 0, unit: '' },
    costPerHr: 0
  });
  const [voucherTypeData, setVoucherTypeData] = useState([]);
  const [top10ItemsPurchased, setTop10ItemsPurchased] = useState([]);
  const [countBySupplier, setCountBySupplier] = useState([]);
  const [spareUptimeByItem, setSpareUptimeByItem] = useState([]);
  const [costPerHrByItem, setCostPerHrByItem] = useState([]);
  const [sumOfAmountByDate, setSumOfAmountByDate] = useState([]);
  const [countOfItemNameByDate, setCountOfItemNameByDate] = useState([]);
  const [sumOfAmountResolution, setSumOfAmountResolution] = useState('day');
  const [countOfItemsResolution, setCountOfItemsResolution] = useState('day');
  const [sumOfAmountDataMap, setSumOfAmountDataMap] = useState({ day: [], month: [], quarter: [], year: [] });
  const [countOfItemsDataMap, setCountOfItemsDataMap] = useState({ day: [], month: [], quarter: [], year: [] });

  // Track if Spares component has loaded its initial data
  const [sparesInitialDataLoaded, setSparesInitialDataLoaded] = useState(false);

  // Track previous manualTrigger to detect when arrow button is clicked
  const prevManualTriggerRef = useRef(manualTrigger);

  // Track previous selectedVoucherType to detect drill-down changes
  const prevSelectedVoucherTypeRef = useRef(selectedVoucherType);

  // Track previous selectedItemName to detect drill-down changes
  const prevSelectedItemNameRef = useRef(selectedItemName);

  // Track previous selectedSupplierName to detect drill-down changes
  const prevSelectedSupplierNameRef = useRef(selectedSupplierName);

  // Track if API call is in progress to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Track if initial call has been made to prevent duplicate initial calls
  const initialCallMadeRef = useRef(false);

  // Fetch spares analytics data from API
  useEffect(() => {
    const fetchSparesData = async () => {
      // Prevent duplicate calls
      if (isFetchingRef.current) {
        console.log('?? API call already in progress, skipping duplicate request');
        return;
      }

      // Only fetch if Spares is the active tab
      if (activeTab !== 'Spares') {
        console.log('?? Skipping Spares API call - not active tab');
        return;
      }

      // Require companyId to be selected
      if (!filters.selectedCompany) {
        console.log('?? Skipping API call - company not selected');
        setLoading(false);
        return;
      }

      // Check if drill-down changed (voucher type, item name, or supplier name clicked)
      const drillDownChanged = selectedVoucherType !== prevSelectedVoucherTypeRef.current || selectedItemName !== prevSelectedItemNameRef.current || selectedSupplierName !== prevSelectedSupplierNameRef.current;

      // After initial load, only trigger API if:
      // 1. Manual trigger is activated (arrow button clicked), OR
      // 2. Drill-down changed (voucher type or item name clicked)
      if (sparesInitialDataLoaded) {
        // Allow API call if drill-down changed
        if (drillDownChanged) {
          prevSelectedVoucherTypeRef.current = selectedVoucherType;
          prevSelectedItemNameRef.current = selectedItemName;
          prevSelectedSupplierNameRef.current = selectedSupplierName;
          console.log('?? Drill-down changed - proceeding with API call for:', { voucherType: selectedVoucherType, item: selectedItemName, supplier: selectedSupplierName });
        } else {
          // Check if manualTrigger actually changed (arrow button was clicked)
          if (manualTrigger === prevManualTriggerRef.current) {
            console.log('?? Skipping auto API call - waiting for manual trigger (arrow button)');
            return;
          }
          // Manual trigger changed, update ref and proceed with API call
          prevManualTriggerRef.current = manualTrigger;
          console.log('?? Manual trigger activated - proceeding with API call');
        }
      } else {
        // On initial load, check if we've already made the initial call
        if (initialCallMadeRef.current) {
          console.log('?? Initial API call already made, skipping duplicate');
          return;
        }
        // Mark that initial call is being made
        initialCallMadeRef.current = true;
        prevSelectedVoucherTypeRef.current = selectedVoucherType;
        prevSelectedItemNameRef.current = selectedItemName;
        prevSelectedSupplierNameRef.current = selectedSupplierName;
      }

      // Set fetching flag
      isFetchingRef.current = true;

      try {
        setLoading(true);
        console.log('?? Fetching spares analytics data with filters...');

        // Get date range from filters or use default (this month start to today)
        const today = new Date();
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

        const startDate = filters.dateRange?.from
          ? formatDateForApi(filters.dateRange.from)
          : formatDateForApi(thisMonthStart);
        const endDate = filters.dateRange?.to
          ? formatDateForApi(filters.dateRange.to)
          : formatDateForApi(today);

        // Build API filters
        const apiFilters = {};

        // Add company ID (required)
        if (filters.selectedCompany) {
          apiFilters.companyId = filters.selectedCompany;
        }

        // Add date range - API uses fromDate and toDate
        if (startDate) {
          apiFilters.fromDate = startDate;
        }
        if (endDate) {
          apiFilters.toDate = endDate;
        }

        // Add optional filters ONLY if they are selected
        if (filters.selectedLocation) {
          apiFilters.locationId = filters.selectedLocation;
          console.log('?? Location filter:', filters.selectedLocation);
        }
        if (filters.selectedMachine) {
          apiFilters.equipmentId = filters.selectedMachine;
          console.log('?? Machine filter:', filters.selectedMachine, '(mapped to equipmentId)');
        }

        // Add voucherType for drill-down functionality
        if (selectedVoucherType) {
          apiFilters.voucherType = selectedVoucherType;
          console.log(' Drill-down active for voucherType:', selectedVoucherType);
        }

        // Add item for drill-down functionality
        if (selectedItemName) {
          apiFilters.item = selectedItemName;
          console.log(' Drill-down active for item:', selectedItemName);
        }

        // Add supplier for drill-down functionality
        if (selectedSupplierName) {
          apiFilters.supplier = selectedSupplierName;
          console.log(' Drill-down active for supplier:', selectedSupplierName);
        }

        console.log('??? Spares API filters prepared:', apiFilters);

        const response = await sparesAnalyticsAPI.getAnalytics(apiFilters);

        // Mark initial data as loaded after first successful API call
        if (!sparesInitialDataLoaded) {
          setSparesInitialDataLoaded(true);
          prevManualTriggerRef.current = manualTrigger;
          prevSelectedVoucherTypeRef.current = selectedVoucherType;
          prevSelectedItemNameRef.current = selectedItemName;
          prevSelectedSupplierNameRef.current = selectedSupplierName;
          console.log('? Initial data loaded - subsequent filter changes require manual trigger');
        } else {
          prevManualTriggerRef.current = manualTrigger;
          prevSelectedVoucherTypeRef.current = selectedVoucherType;
          prevSelectedItemNameRef.current = selectedItemName;
          prevSelectedSupplierNameRef.current = selectedSupplierName;
        }

        if (response && response.success) {
          console.log('? Spares analytics data received:', response);

          // Map summary data
          if (response.summary) {
            setKeyMetrics({
              totalExpenditure: {
                value: parseFloat(response.summary.total_expenditure_formatted?.replace(/[^\d.]/g, '')) || 0,
                suffix: response.summary.total_expenditure_formatted?.includes('M') ? 'M' :
                  response.summary.total_expenditure_formatted?.includes('K') ? 'K' : '',
                unit: 'Rupees'
              },
              uptimeOfSpares: {
                value: response.summary.uptime_of_spares?.hmr || 0,
                unit: 'HMR'
              },
              avgAmtSpendOnSpares: {
                value: parseFloat(response.summary.avg_amt_spend_formatted?.replace(/[^\d.]/g, '')) || 0,
                unit: 'Rupees'
              },
              totalNoOfSparesOrdered: {
                value: response.summary.total_no_of_spares_ordered_formatted || 0,
                suffix: '',
                unit: ''
              },
              finalMetric: {
                value: response.summary.total_no_of_suppliers_formatted || 0,
                unit: ''
              },
              costPerHr: response.summary.cost_per_hr || 0
            });
          }

          // Update available machines from equipment_ids in the response
          const equipmentIds = response.equipment_ids || response.summary?.equipment_ids || [];
          if (equipmentIds.length > 0) {
            // Try to fetch equipment names from the equipment API
            const companyId = filters.selectedCompany ||
              localStorage.getItem('companyId') || sessionStorage.getItem('companyId');

            if (companyId) {
              try {
                const fullEquipmentList = await equipmentAPI.getEquipment(companyId);
                if (fullEquipmentList && Array.isArray(fullEquipmentList)) {
                  // Cross-reference: filter full list to only those IDs in the response
                  const equipmentIdSet = new Set(equipmentIds.map(id => id.toString()));
                  const filteredMachines = fullEquipmentList.filter(eq =>
                    equipmentIdSet.has(eq.machineId.toString())
                  );

                  if (filteredMachines.length > 0) {
                    setAvailableMachines(filteredMachines);
                    console.log('🔧 Spares: Updated available machines with names:', filteredMachines.length);
                  } else {
                    // Fallback: use IDs directly if no name match found
                    const machinesFromIds = equipmentIds.map(id => ({
                      machineId: id,
                      machineName: `Equipment ${id}`
                    }));
                    setAvailableMachines(machinesFromIds);
                    console.log('🔧 Spares: Fallback - using equipment IDs:', machinesFromIds.length);
                  }
                }
              } catch (err) {
                console.warn('⚠️ Could not fetch equipment names, using IDs:', err.message);
                const machinesFromIds = equipmentIds.map(id => ({
                  machineId: id,
                  machineName: `Equipment ${id}`
                }));
                setAvailableMachines(machinesFromIds);
              }
            } else {
              // No company ID: just use raw IDs
              const machinesFromIds = equipmentIds.map(id => ({
                machineId: id,
                machineName: `Equipment ${id}`
              }));
              setAvailableMachines(machinesFromIds);
            }
          }

          // Map graphs data
          if (response.graphs) {
            // Voucher type data
            if (response.graphs.voucher_type && Array.isArray(response.graphs.voucher_type)) {
              const colors = ['#004481', '#0096FF', '#F28E43', '#ec4899', '#8b5cf6'];
              const mapped = response.graphs.voucher_type
                .map((item, index) => ({
                  name: item.name,
                  value: item.value || 0,
                  percentage: typeof item.percentage === 'string' ? item.percentage : `${item.percentage || 0}%`,
                  fill: colors[index % colors.length]
                }));
              setVoucherTypeData(mapped);
            }

            // Top 10 items purchased
            if (response.graphs.top_10_items_purchased && Array.isArray(response.graphs.top_10_items_purchased)) {
              const colors = ['#004481', '#00FBFF', '#00C3FF', '#2D89FF', '#C7C7C7', '#FF4D4D', '#FF33DA', '#8A2BE2', '#F28E43', '#A0A0A0'];
              const mapped = response.graphs.top_10_items_purchased
                .map((item, index) => ({
                  name: item.name,
                  value: item.value || 0,
                  percentage: typeof item.percentage === 'string' ? item.percentage : `${item.percentage || 0}%`,
                  fill: colors[index % colors.length]
                }));
              setTop10ItemsPurchased(mapped);
            }

            // Count by supplier
            if (response.graphs.item_count_by_supplier && Array.isArray(response.graphs.item_count_by_supplier)) {
              const mapped = response.graphs.item_count_by_supplier.map(item => ({
                name: `${item.supplier_name} - ${item.item_name}`,
                item_name: item.item_name,
                supplier_name: item.supplier_name,
                count: item.count
              }));
              setCountBySupplier(mapped);
            }

            // Spare uptime by item
            if (response.graphs.spare_uptime_2_by_item && Array.isArray(response.graphs.spare_uptime_2_by_item)) {
              const mapped = response.graphs.spare_uptime_2_by_item.map(item => ({
                name: item.name || item.item_name,
                uptime: item.uptime || item.spare_uptime_2 || item.value || 0
              }));
              setSpareUptimeByItem(mapped);
            }

            // Cost per hour by item
            if (response.graphs.cost_per_hr_by_item && Array.isArray(response.graphs.cost_per_hr_by_item)) {
              const mapped = response.graphs.cost_per_hr_by_item.map(item => ({
                name: item.name || item.item_name,
                costPerHr: item.costPerHr || item.cost_per_hr || item.value || 0
              }));
              setCostPerHrByItem(mapped);
            }

            // Helper to transform sum_of_amount data supporting both:
            // - New API structure: { current: [...], forecast: [...] }
            // - Legacy structure: flat array [...]
            const transformSumOfAmountData = (graphData) => {
              if (!graphData) return [];

              // New API structure with current and forecast arrays
              if (graphData.current && Array.isArray(graphData.current)) {
                const currentData = graphData.current;
                const forecastData = graphData.forecast || [];

                // Combine current and forecast data by date
                const allDates = new Set([
                  ...currentData.map(item => item.date),
                  ...forecastData.map(item => item.date)
                ]);

                return Array.from(allDates).map(date => {
                  const currentItem = currentData.find(item => item.date === date);
                  const forecastItem = forecastData.find(item => item.date === date);
                  return {
                    date,
                    current: currentItem ? (currentItem.sum_of_amount || 0) : null,
                    forecast: forecastItem ? (forecastItem.sum_of_amount || 0) : null
                  };
                });
              }

              // Legacy structure (direct array)
              if (Array.isArray(graphData)) {
                return graphData.map(item => ({
                  date: item.date,
                  current: item.sum_of_amount || 0,
                  forecast: null
                }));
              }

              return [];
            };

            // Sum of amount by date (combine all date ranges)
            const sumDataMap = { day: [], month: [], quarter: [], year: [] };

            sumDataMap.day = transformSumOfAmountData(response.graphs.sum_of_amount_by_day);
            sumDataMap.month = transformSumOfAmountData(response.graphs.sum_of_amount_by_month);
            sumDataMap.quarter = transformSumOfAmountData(response.graphs.sum_of_amount_by_quarter);
            sumDataMap.year = transformSumOfAmountData(response.graphs.sum_of_amount_by_year);

            setSumOfAmountDataMap(sumDataMap);
            setSumOfAmountByDate(sumDataMap[sumOfAmountResolution]);

            // Count of item name by date
            const countDataMap = { day: [], month: [], quarter: [], year: [] };

            // Helper to transform count_of_items data supporting both:
            // - New API structure: { current: [...], forecast: [...] }
            // - Legacy structure: flat array [...]
            const transformCountOfItemsData = (graphData) => {
              if (!graphData) return [];

              // New API structure with current and forecast arrays
              if (graphData.current && Array.isArray(graphData.current)) {
                const currentData = graphData.current;
                const forecastData = graphData.forecast || [];

                // Combine current and forecast data by date
                const allDates = new Set([
                  ...currentData.map(item => item.date),
                  ...forecastData.map(item => item.date)
                ]);

                return Array.from(allDates).map(date => {
                  const currentItem = currentData.find(item => item.date === date);
                  const forecastItem = forecastData.find(item => item.date === date);
                  return {
                    date,
                    current: currentItem ? (currentItem.count_of_items || 0) : null,
                    forecast: forecastItem ? (forecastItem.count_of_items || 0) : null
                  };
                });
              }

              // Legacy structure (direct array)
              if (Array.isArray(graphData)) {
                return graphData.map(item => ({
                  date: item.date,
                  current: item.count_of_items || 0,
                  forecast: null
                }));
              }

              return [];
            };

            countDataMap.day = transformCountOfItemsData(response.graphs.count_of_items_by_day);
            countDataMap.month = transformCountOfItemsData(response.graphs.count_of_items_by_month);
            countDataMap.quarter = transformCountOfItemsData(response.graphs.count_of_items_by_quarter);
            countDataMap.year = transformCountOfItemsData(response.graphs.count_of_items_by_year);

            setCountOfItemsDataMap(countDataMap);
            setCountOfItemNameByDate(countDataMap[countOfItemsResolution]);
          }

          console.log('? All spares data transformed and set');
        } else {
          console.warn('?? No data received from spares analytics API');
        }
      } catch (error) {
        console.error('? Failed to fetch spares analytics:', error);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchSparesData();
  }, [
    filters.selectedCompany,
    filters.dateRange?.from?.getTime(),
    filters.dateRange?.to?.getTime(),
    filters.selectedMachine,
    filters.selectedLocation,
    manualTrigger,
    selectedVoucherType, // Trigger API when drill-down voucher type changes
    selectedItemName, // Trigger API when drill-down item name changes
    selectedSupplierName, // Trigger API when drill-down supplier name changes
    // NOTE: sparesInitialDataLoaded intentionally NOT included to prevent double API calls
    activeTab
  ]);

  // Handler for voucher type drill-down
  const handleVoucherTypeDrillDown = useCallback((voucherType) => {
    console.log('?? Drill-down clicked for voucher type:', voucherType);
    setSelectedVoucherType(voucherType);
  }, []);

  // Handler to clear voucher type drill-down
  const handleClearVoucherTypeDrillDown = useCallback(() => {
    console.log('?? Clearing voucher type drill-down');
    setSelectedVoucherType(null);
  }, []);


  // Handler for item name drill-down
  const handleItemNameDrillDown = useCallback((itemName) => {
    console.log(' Drill-down clicked for item name:', itemName);
    setSelectedItemName(itemName);
  }, []);

  // Handler to clear item name drill-down
  const handleClearItemNameDrillDown = useCallback(() => {
    console.log(' Clearing item name drill-down');
    setSelectedItemName(null);
  }, []);

  // Handler for supplier name drill-down
  const handleSupplierNameDrillDown = useCallback((supplierName) => {
    console.log(' Drill-down clicked for supplier name:', supplierName);
    setSelectedSupplierName(supplierName);
  }, []);

  // Handler to clear supplier name drill-down
  const handleClearSupplierNameDrillDown = useCallback(() => {
    console.log(' Clearing supplier name drill-down');
    setSelectedSupplierName(null);
  }, []);

  // Sync displayed chart data when resolution changes
  useEffect(() => {
    if (sumOfAmountDataMap[sumOfAmountResolution]) {
      setSumOfAmountByDate(sumOfAmountDataMap[sumOfAmountResolution]);
    }
  }, [sumOfAmountResolution, sumOfAmountDataMap]);

  useEffect(() => {
    if (countOfItemsDataMap[countOfItemsResolution]) {
      setCountOfItemNameByDate(countOfItemsDataMap[countOfItemsResolution]);
    }
  }, [countOfItemsResolution, countOfItemsDataMap]);

  // Format value for display
  const formatValue = (value, suffix) => {
    if (suffix) {
      return `${value.toFixed(2)}${suffix}`;
    }
    return value.toLocaleString();
  };

  // Format unit - convert "Rupees" to rupee symbol
  const formatUnit = (unit) => {
    if (unit === 'Rupees' || unit === 'rupees') {
      return '?';
    }
    return unit;
  };

  // Format donut chart label - improved visibility with anti-overlap
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value, payload }) => {
    // Hide labels for small segments (e.g., < 5%) to prevent cluster overlap in small dashboard cards
    if (value === 0 || percent < 0.05) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 15;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const percentage = payload?.percentage || `${(percent * 100).toFixed(1)}%`;
    const displayValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value;

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '9px', fontWeight: 600 }}
      >
        {`${displayValue} (${percentage})`}
      </text>
    );
  };

  // Format donut chart label for maximized view - with smart spacing to prevent overlap
  const labelPositions = useRef([]);

  const renderMaximizedCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value, payload, index }) => {
    // Show all labels in maximized view, only hide absolute zeros
    if (value === 0) return null;

    const RADIAN = Math.PI / 180;
    const LINE_HEIGHT = 16; // Minimum space between labels

    // Calculate initial position
    const baseRadius = outerRadius + 30;
    const baseX = cx + baseRadius * Math.cos(-midAngle * RADIAN);
    const baseY = cy + baseRadius * Math.sin(-midAngle * RADIAN);

    // Determine which side the label is on
    const isRightSide = baseX > cx;

    // For labels on the same side, check for overlaps and adjust
    let adjustedY = baseY;
    const extendedRadius = baseRadius + 20;

    // Check overlap with previous labels on the same side
    if (labelPositions.current.length > 0) {
      const sameSideLabels = labelPositions.current.filter(pos =>
        (isRightSide && pos.x > cx) || (!isRightSide && pos.x <= cx)
      );

      for (const prevPos of sameSideLabels) {
        const distance = Math.abs(adjustedY - prevPos.y);
        if (distance < LINE_HEIGHT) {
          // Push this label down/up to avoid overlap
          if (adjustedY >= prevPos.y) {
            adjustedY = prevPos.y + LINE_HEIGHT;
          } else {
            adjustedY = prevPos.y - LINE_HEIGHT;
          }
        }
      }
    }

    // Store this label's position for next labels to check
    if (index === 0) {
      labelPositions.current = [];
    }
    labelPositions.current.push({ x: baseX, y: adjustedY, index });

    // Calculate extended line endpoint
    const labelX = isRightSide
      ? cx + extendedRadius
      : cx - extendedRadius;

    const percentage = payload?.percentage || `${(percent * 100).toFixed(2)}%`;
    const displayValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(2);

    // Calculate line segments
    const lineEndX = cx + (outerRadius + 8) * Math.cos(-midAngle * RADIAN);
    const lineEndY = cy + (outerRadius + 8) * Math.sin(-midAngle * RADIAN);
    const lineMidX = cx + (outerRadius + 20) * Math.cos(-midAngle * RADIAN);

    return (
      <g>
        {/* Connector line from pie to label */}
        <path
          d={`M ${lineEndX} ${lineEndY} L ${lineMidX} ${adjustedY} L ${labelX} ${adjustedY}`}
          fill="none"
          stroke="#9ca3af"
          strokeWidth={1}
        />
        <text
          x={labelX + (isRightSide ? 4 : -4)}
          y={adjustedY}
          fill="#374151"
          textAnchor={isRightSide ? 'start' : 'end'}
          dominantBaseline="central"
          style={{ fontSize: '10px', fontWeight: 600 }}
        >
          {`${displayValue} (${percentage})`}
        </text>
      </g>
    );
  };

  // Chart configurations - maps chart IDs to their properties
  // TODO: When API is ready, follow these steps:
  // 1. Uncomment the apiEndpoint property in each chart config below
  // 2. Update the apiEndpoint URLs to match your API routes
  // 3. Replace the handleMaximize function with the async version (see commented code below)
  // 4. Remove or comment out the mockData import if no longer needed
  const chartConfigs = {
    'voucher-type-data': {
      title: 'No of Items based on Voucher Type',
      dataKey: 'voucherTypeData',
      // apiEndpoint: '/api/spares/voucher-type-data',
      chartType: 'pie',
      chartProps: {
        innerRadius: 60,
        outerRadius: 80,
        paddingAngle: 5,
        dataKey: 'value',
        label: renderCustomLabel,
        showLegend: true
      }
    },
    'top-10-items-purchased': {
      title: 'Top 10 Items Purchased',
      dataKey: 'top10ItemsPurchased',
      // apiEndpoint: '/api/spares/top-10-items-purchased',
      chartType: 'pie',
      chartProps: {
        innerRadius: 0,
        outerRadius: 80,
        dataKey: 'value',
        label: renderCustomLabel,
        showLegend: true
      }
    },
    'count-by-supplier': {
      title: 'Count of item Name by Supplier Name',
      dataKey: 'countBySupplier',
      // apiEndpoint: '/api/spares/count-by-supplier',
      chartType: 'bar',
      chartProps: {
        barCategoryGap: '10%',
        dataKey: 'count',
        fill: '#ef4444',
        radius: [4, 4, 0, 0],
        barSize: 34.94,
        referenceLine: { y: 110, stroke: '#ef4444', strokeDasharray: '3 3' },
        xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: 'Supplier Name', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: { tick: { fontSize: 12 }, label: { value: 'Count Item Name', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
      }
    },
    'spare-uptime-by-item': {
      title: 'Spare_Uptime_2 by Item Name',
      dataKey: 'spareUptimeByItem',
      // apiEndpoint: '/api/spares/spare-uptime-by-item',
      chartType: 'bar',
      chartProps: {
        barCategoryGap: '10%',
        dataKey: 'uptime',
        fill: '#3b82f6',
        radius: [4, 4, 0, 0],
        barSize: 34.94,
        referenceLine: { y: 1100, stroke: '#ef4444', strokeDasharray: '3 3' },
        xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: 'Item Name', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value / 1000}K`,
          label: { value: 'Spare_Uptime_2', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        }
      }
    },
    'cost-per-hr-by-item': {
      title: 'Cost/Hr by Item Name',
      dataKey: 'costPerHrByItem',
      // apiEndpoint: '/api/spares/cost-per-hr-by-item',
      chartType: 'bar',
      chartProps: {
        barCategoryGap: '10%',
        dataKey: 'costPerHr',
        fill: '#ec4899',
        radius: [4, 4, 0, 0],
        barSize: 34.94,
        referenceLine: { y: 5, stroke: '#ef4444', strokeDasharray: '3 3' },
        xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: 'Item Name', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: { tick: { fontSize: 12 }, label: { value: 'Cost/Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } }
      }
    },
    'sum-of-amount-by-date': {
      title: 'Sum of amt by Yr, Quarter, Month and Day',
      dataKey: 'sumOfAmountByDate',
      // apiEndpoint: '/api/spares/sum-of-amount-by-date',
      chartType: 'line',
      chartProps: {
        xAxis: { dataKey: 'date', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value}M`,
          label: { value: 'Sum of Amount', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ],
        referenceLine: { y: 1, stroke: '#000', strokeDasharray: '3 3' }
      }
    },
    'count-of-item-name-by-date': {
      title: 'Count of item name by Yr, Quarter, Month and Day',
      dataKey: 'countOfItemNameByDate',
      // apiEndpoint: '/api/spares/count-of-item-name-by-date',
      chartType: 'line',
      chartProps: {
        xAxis: { dataKey: 'date', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value}M`,
          label: { value: 'No of Items', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ],
        referenceLine: { y: 1, stroke: '#000', strokeDasharray: '3 3' }
      }
    }
  };

  // Handler for maximize button - ready for API integration
  // TODO: Replace mockData access with API call when ready
  // Example API integration:
  // const handleMaximize = useCallback(async (chartId) => {
  //   const config = chartConfigs[chartId];
  //   if (!config) return;
  //
  //   setLoading(true);
  //   try {
  //     const response = await fetch(config.apiEndpoint, {
  //       method: 'GET',
  //       headers: { 'Content-Type': 'application/json' },
  //       // Add any filters/params: { limit: null, sort: 'low-high' }
  //     });
  //     const data = await response.json();
  //     setChartData(prev => ({
  //       ...prev,
  //       spares: {
  //         ...prev.spares,
  //         [config.dataKey]: data.result || data
  //       }
  //     }));
  //     setMaximizedChartId(chartId);
  //   } catch (error) {
  //     console.error('Error fetching chart data:', error);
  //     // Fallback to mock data on error
  //     setMaximizedChartId(chartId);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);
  const handleMaximize = useCallback((chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return;

    // Currently using mockData - will be replaced with API call
    setMaximizedChartId(chartId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setMaximizedChartId(null);
    setModalEquipmentFilter('all'); // Reset filter when closing modal
    setModalSortOrder('low-high'); // Reset sort order when closing modal
  }, []);

  // Get unique item/supplier list for the current chart
  const getEquipmentListForChart = useCallback((chartId) => {
    let dataSource = [];

    if (chartId === 'voucher-type-data') {
      dataSource = voucherTypeData || [];
    } else if (chartId === 'top-10-items-purchased') {
      dataSource = top10ItemsPurchased || [];
    } else if (chartId === 'count-by-supplier') {
      dataSource = countBySupplier || [];
    } else if (chartId === 'spare-uptime-by-item') {
      dataSource = spareUptimeByItem || [];
    } else if (chartId === 'cost-per-hr-by-item') {
      dataSource = costPerHrByItem || [];
    }

    return [...new Set(dataSource.map(item => item.name).filter(Boolean))];
  }, [voucherTypeData, top10ItemsPurchased, countBySupplier, spareUptimeByItem, costPerHrByItem]);

  // Get filter label based on chart type
  const getFilterLabelForChart = (chartId) => {
    if (chartId === 'voucher-type-data') {
      return 'All Voucher Types';
    } else if (chartId === 'count-by-supplier') {
      return 'All Suppliers';
    } else if (['top-10-items-purchased', 'spare-uptime-by-item', 'cost-per-hr-by-item'].includes(chartId)) {
      return 'All Items';
    }
    return 'All Equipment';
  };

  // Render maximized chart component
  const renderMaximizedChart = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    // Get data from state based on chartId
    let data = [];
    if (chartId === 'voucher-type-data') {
      data = voucherTypeData || [];
    } else if (chartId === 'top-10-items-purchased') {
      data = top10ItemsPurchased || [];
    } else if (chartId === 'count-by-supplier') {
      data = countBySupplier || [];
    } else if (chartId === 'spare-uptime-by-item') {
      data = spareUptimeByItem || [];
    } else if (chartId === 'cost-per-hr-by-item') {
      data = costPerHrByItem || [];
    } else if (chartId === 'sum-of-amount-by-date') {
      data = sumOfAmountByDate || [];
    } else if (chartId === 'count-of-item-name-by-date') {
      data = countOfItemNameByDate || [];
    }

    // Store original data for pie chart proportion calculation
    let originalData = [...data];

    // Apply filter - for pie charts, we handle this differently to maintain proportions
    if (modalEquipmentFilter !== 'all') {
      if (config.chartType === 'pie') {
        // For pie charts, show selected item with actual proportion + "Others"
        const selectedItem = data.find(item => item.name === modalEquipmentFilter);
        if (selectedItem) {
          const otherItems = data.filter(item => item.name !== modalEquipmentFilter);
          const otherValue = otherItems.reduce((sum, item) => sum + (item.value || 0), 0);

          // Create data with selected item and "Others" to maintain proportions
          data = [
            selectedItem,
            {
              name: 'Others (Rest)',
              value: otherValue,
              fill: '#d1d5db', // Gray color for others
              percentage: null // Will be calculated in label renderer
            }
          ].filter(item => item.value > 0);
        }
      } else {
        // For bar/line charts, filter normally
        data = data.filter(item => item.name === modalEquipmentFilter);
      }
    }

    // Apply sorting for pie charts
    if (config.chartType === 'pie' && data.length > 0) {
      const sortKey = config.chartProps?.dataKey || 'value';
      data = [...data].sort((a, b) => {
        const aValue = a[sortKey] || 0;
        const bValue = b[sortKey] || 0;
        return modalSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
      });
    }

    // Apply sorting for bar charts
    if (config.chartType === 'bar' && data.length > 0) {
      const sortKey = config.chartProps?.dataKey || 'value';
      data = [...data].sort((a, b) => {
        const aValue = Number(a[sortKey]) || 0;
        const bValue = Number(b[sortKey]) || 0;
        return modalSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
      });
    }

    if (config.chartType === 'pie') {
      const props = config.chartProps;
      // Use responsive radius for maximized view
      // Voucher Type -> donut chart, Top 10 Items -> solid pie
      const mInnerRadius = chartId === 'voucher-type-data' ? 70 : 0;
      // Dynamic radius: larger for 2xl screens (TVs), standard for desktop
      const isTV = typeof window !== 'undefined' && window.innerWidth >= 1920;
      const mOuterRadius = isTV ? 180 : 130;

      // Calculate total for percentages
      const total = data.reduce((sum, d) => sum + (d.value || 0), 0);

      // Custom label renderer with automatic line extension for better spacing
      const renderPieLabel = (labelProps) => {
        const { cx, cy, midAngle, outerRadius, value, payload, index } = labelProps;
        if (!value || value === 0) return null;

        const RADIAN = Math.PI / 180;
        const percent = value / total;

        // Calculate base extension - smaller segments get longer lines
        const baseExtension = isTV ? 25 : 15;
        const extraExtension = percent < 0.01 ? 60 : percent < 0.05 ? 40 : percent < 0.1 ? 30 : 15;
        const totalExtension = baseExtension + extraExtension + (index % 3) * 10; // Stagger lines

        const radius = outerRadius + totalExtension;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        // Line start point (at pie edge)
        const lineStartX = cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN);
        const lineStartY = cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN);

        // Mid point for the elbow
        const midRadius = outerRadius + totalExtension - 20;
        const midX = cx + midRadius * Math.cos(-midAngle * RADIAN);
        const midY = cy + midRadius * Math.sin(-midAngle * RADIAN);

        const isRightSide = x > cx;
        const percentage = payload?.percentage || `${(percent * 100).toFixed(2)}%`;
        const displayValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(2);

        return (
          <g>
            {/* Connecting line with elbow */}
            <path
              d={`M ${lineStartX} ${lineStartY} L ${midX} ${midY} L ${x} ${y}`}
              fill="none"
              stroke="#9ca3af"
              strokeWidth={1}
            />
            {/* Small dot at the segment edge */}
            <circle cx={lineStartX} cy={lineStartY} r={2} fill="#9ca3af" />
            {/* Label text */}
            <text
              x={x + (isRightSide ? 5 : -5)}
              y={y}
              fill="#374151"
              textAnchor={isRightSide ? 'start' : 'end'}
              dominantBaseline="central"
              style={{ fontSize: '13px', fontWeight: 600 }} // Increased from 10px
            >
              {`${displayValue} (${percentage})`}
            </text>
          </g>
        );
      };

      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={mInnerRadius}
              outerRadius={mOuterRadius}
              dataKey={props.dataKey}
              label={renderPieLabel}
              labelLine={false}
              paddingAngle={1}
              stroke="#fff"
              strokeWidth={1}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.fill}
                  style={{ cursor: (chartId === 'voucher-type-data' || chartId === 'top-10-items-purchased') ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (chartId === 'voucher-type-data') {
                      handleVoucherTypeDrillDown(entry.name);
                      handleCloseModal();
                    } else if (chartId === 'top-10-items-purchased') {
                      handleItemNameDrillDown(entry.name);
                      handleCloseModal();
                    }
                  }}
                />
              ))}
            </Pie>
            <Tooltip />
            {props.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={50}
                onClick={(e) => {
                  // Get the item name from the legend click
                  const itemName = e.value;
                  if (itemName && itemName !== 'Others (Rest)') {
                    // Toggle selection: if already selected, go back to "all"
                    if (modalEquipmentFilter === itemName) {
                      setModalEquipmentFilter('all');
                    } else {
                      setModalEquipmentFilter(itemName);
                    }
                  }
                }}
                wrapperStyle={{ cursor: 'pointer' }}
                formatter={(value, entry) => {
                  const isSelected = modalEquipmentFilter === value;
                  const isOthers = value === 'Others (Rest)';
                  return (
                    <span
                      className={`text-xs font-medium ${isSelected
                        ? 'text-blue-600 font-bold underline'
                        : isOthers
                          ? 'text-gray-400'
                          : 'text-gray-700 hover:text-blue-500'
                        }`}
                      style={{ cursor: isOthers ? 'default' : 'pointer' }}
                    >
                      {entry.payload.name}
                    </span>
                  );
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      );
    } else if (config.chartType === 'bar') {
      const props = config.chartProps;

      // Calculate minimum width based on number of bars and label lengths - ensure full labels fit
      const maxLabelLength = data.length > 0 ? Math.max(...data.map(item => ((item.name || item[props.xAxis?.dataKey || 'name']) || '').length), 15) : 15;
      // Calculate width to fit full labels: ~10px per character for horizontal text
      const estimatedWidthPerBar = Math.max(120, maxLabelLength * 10);
      const minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);
      const maximizedBarSize = 48; // Reduced from 62.29
      const maximizedBarCategoryGap = '25%'; // Reduced from 35%

      // Ensure margin includes left space for Y-axis labels and bottom space for X-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 10,
        left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
        bottom: chartMargin.bottom ?? 30
      };

      // Create modified axis props without labels (we'll render them separately as fixed overlays)
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      // Format X-axis tick labels - show full labels horizontally (straight line)
      const xAxisPropsWithFormatter = {
        ...xAxisPropsWithoutLabel,
        tick: {
          ...xAxisPropsWithoutLabel.tick,
          fontSize: 14, // Increased from 11
          angle: 0, // Horizontal labels (straight line)
          textAnchor: 'middle',
          height: 60
        },
        height: 60 // Height for horizontal labels
        // No tickFormatter - show full labels without truncation
      };

      // Bottom margin for horizontal labels (less space needed)
      defaultMargin.bottom = Math.max(defaultMargin.bottom, 30);

      return (
        <div className="w-full h-full relative flex flex-col" key={`chart-container-${chartId}-${modalSortOrder}`}>
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px',
                paddingBottom: props.xAxis?.label ? '30px' : '0'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 11px
                  fontWeight: 600, // Increased from 500
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll"
            style={{
              marginLeft: props.yAxis?.label ? '60px' : '0',
              marginBottom: props.xAxis?.label ? '30px' : '0'
            }}
          >
            <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barCategoryGap={maximizedBarCategoryGap} margin={defaultMargin}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis {...xAxisPropsWithFormatter} />
                  <YAxis {...yAxisPropsWithoutLabel} />
                  <Tooltip
                    cursor={false}
                    formatter={(value, name, props) => {
                      // Show full label in tooltip - props.payload contains the data point
                      const fullLabel = props.payload?.name || props.payload?.[props.xAxis?.dataKey || 'name'] || name;
                      return [value, fullLabel];
                    }}
                    labelFormatter={(label) => {
                      // Find the full label from data
                      const dataKey = props.xAxis?.dataKey || 'name';
                      const dataItem = data.find(item => (item.name || item[dataKey]) === label);
                      return dataItem ? `Item: ${dataItem.name || dataItem[dataKey]}` : label;
                    }}
                  />
                  {props.referenceLine && (
                    <ReferenceLine
                      y={props.referenceLine.y}
                      stroke={props.referenceLine.stroke}
                      strokeDasharray={props.referenceLine.strokeDasharray}
                    />
                  )}
                  <Bar
                    dataKey={props.dataKey}
                    fill={props.fill}
                    radius={props.radius}
                    barSize={maximizedBarSize}
                    activeBar={false}
                    onClick={(data) => {
                      if (['spare-uptime-by-item', 'cost-per-hr-by-item'].includes(chartId) && data && data.name) {
                        handleItemNameDrillDown(data.name);
                        handleCloseModal();
                        console.log('Clicked on bar item:', data.name);
                      } else if (chartId === 'count-by-supplier' && data && data.supplier_name) {
                        handleSupplierNameDrillDown(data.supplier_name);
                        handleCloseModal();
                        console.log('Clicked on supplier bar, supplier:', data.supplier_name);
                      }
                    }}
                    style={{ cursor: ['spare-uptime-by-item', 'cost-per-hr-by-item', 'count-by-supplier'].includes(chartId) ? 'pointer' : 'default' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fixed X-axis label - positioned absolutely at the bottom */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: props.yAxis?.label ? '60px' : '0',
                fontSize: '14px', // Increased from 12px
                fontWeight: 600, // Increased from 500
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}
        </div>
      );
    } else if (config.chartType === 'line') {
      const props = config.chartProps;
      // Determine colors for indicators based on chart type
      const currentColor = props.lines?.[0]?.stroke || '#3b82f6';
      const forecastColor = props.lines?.[1]?.stroke || '#f97316';

      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 60, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...props.xAxis} />
                <YAxis
                  {...props.yAxis}
                  label={{
                    ...props.yAxis.label,
                    offset: -40,
                    position: 'insideLeft'
                  }}
                />
                <Tooltip />
                {props.lines.map((line, index) => (
                  <Line
                    key={index}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.stroke}
                    strokeWidth={line.strokeWidth}
                    strokeDasharray={line.strokeDasharray}
                    dot={line.dot}
                  />
                ))}
                {props.referenceLine && (
                  <ReferenceLine
                    y={props.referenceLine.y}
                    stroke={props.referenceLine.stroke}
                    strokeDasharray={props.referenceLine.strokeDasharray}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 px-6 pb-4 pt-2">
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: currentColor }}></span>
              Current
            </div>
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: forecastColor }}></span>
              Forecast
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Reusable Maximize Button Component
  const MaximizeButton = ({ chartId, className = "p-2 hover:bg-gray-100 rounded-lg transition-colors" }) => (
    <button
      onClick={() => handleMaximize(chartId)}
      className={className}
      aria-label="Maximize chart"
    >
      <Maximize2 className="w-4 h-4 text-gray-600" />
    </button>
  );

  // Render header actions (dropdown selectors) for maximized charts
  const renderHeaderActions = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    if (chartId === 'sum-of-amount-by-date') {
      return (
        <Select value={sumOfAmountResolution} onValueChange={setSumOfAmountResolution}>
          <SelectTrigger className="w-[120px] h-8 text-xs bg-white">
            <SelectValue placeholder="Select Resolution" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">By Day</SelectItem>
            <SelectItem value="month">By Month</SelectItem>
            <SelectItem value="quarter">By Quarter</SelectItem>
            <SelectItem value="year">By Year</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (chartId === 'count-of-item-name-by-date') {
      return (
        <Select value={countOfItemsResolution} onValueChange={setCountOfItemsResolution}>
          <SelectTrigger className="w-[120px] h-8 text-xs bg-white">
            <SelectValue placeholder="Select Resolution" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">By Day</SelectItem>
            <SelectItem value="month">By Month</SelectItem>
            <SelectItem value="quarter">By Quarter</SelectItem>
            <SelectItem value="year">By Year</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    // Charts that have dropdown selectors (bar charts)
    if (config.chartType === 'bar' || config.chartType === 'horizontalBar') {
      return (
        <Select value={modalSortOrder} onValueChange={setModalSortOrder}>
          <SelectTrigger className="w-[140px] h-9 text-xs bg-gray-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low-high">Low to High</SelectItem>
            <SelectItem value="high-low">High to Low</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}

      {/* Key Metrics Section */}
      <div className="px-3 md:px-4 pt-2 pb-1 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {/* Total Expenditure */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Total Expenditure</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {formatValue(keyMetrics.totalExpenditure.value, keyMetrics.totalExpenditure.suffix)}
              </span>
              <span className="text-[15px] mt-1 text-gray-500">₹</span>
            </div>
          </div>

          {/* Uptime of Spares */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Uptime of Spares</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.uptimeOfSpares.value.toFixed(2)}
              </span>
              <span className="text-[12px] mt-1 text-gray-500">{formatUnit(keyMetrics.uptimeOfSpares.unit)}</span>
            </div>
          </div>

          {/* Avg amt spend on Spares */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Avg Spend on Spares</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.avgAmtSpendOnSpares.value.toFixed(2)}
              </span>
              <span className="text-[15px] mt-1 text-gray-500">₹</span>
            </div>
          </div>

          {/* Total No of Spares Ordered */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Spares Ordered</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {formatValue(keyMetrics.totalNoOfSparesOrdered.value, keyMetrics.totalNoOfSparesOrdered.suffix)}
              </span>
            </div>
          </div>

          {/* Total no of Suppliers */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Total Suppliers</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.finalMetric.value}
              </span>
            </div>
          </div>

          {/* Cost Per Hr */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Cost/Hr</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.costPerHr}
              </span>
              <span className="text-[15px] mt-1 text-gray-500">₹</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="flex-1 px-3 md:px-4 pb-4 bg-gray-50">
        {/* Active Drill-down Filters - UI indication */}
        {(selectedVoucherType || selectedItemName || selectedSupplierName) && (
          <div className="flex items-center gap-2 mb-3 mt-1">
            <div className="text-[12px] font-medium text-gray-500">Active Drill-down:</div>
            {selectedVoucherType && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
                <span className="font-bold">Voucher Type:</span> {selectedVoucherType}
                <button
                  onClick={handleClearVoucherTypeDrillDown}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  title="Clear voucher filter"
                >
                  <X className="w-3 h-3 text-blue-700" />
                </button>
              </div>
            )}
            {selectedItemName && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
                <span className="font-bold">Item:</span> {selectedItemName}
                <button
                  onClick={handleClearItemNameDrillDown}
                  className="hover:bg-emerald-200 rounded-full p-0.5 transition-colors"
                  title="Clear item filter"
                >
                  <X className="w-3 h-3 text-emerald-700" />
                </button>
              </div>
            )}
            {selectedSupplierName && (
              <div className="bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
                <span className="font-bold">Supplier:</span> {selectedSupplierName}
                <button
                  onClick={handleClearSupplierNameDrillDown}
                  className="hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                  title="Clear supplier filter"
                >
                  <X className="w-3 h-3 text-orange-700" />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-3 pt-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">

          {/* Voucher Type Pie Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">No of Items based on Voucher Type</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="voucher-type-data" />
              </div>
            </div>
            <div className="flex-1 p-2 flex overflow-hidden">
              <div className="flex-[1.5] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={voucherTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={28}
                      outerRadius={40}
                      paddingAngle={4}
                      dataKey="value"
                      labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                      label={renderCustomLabel}
                      onClick={(data) => {
                        if (data && data.name) handleVoucherTypeDrillDown(data.name);
                      }}
                    >
                      {voucherTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.fill}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleVoucherTypeDrillDown(entry.name)}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 flex flex-col gap-1 ml-1 min-w-0 overflow-y-auto pr-1 custom-scrollbar">
                <h4 className="text-[10px] font-bold text-gray-900 mb-1">Voucher Type</h4>
                {voucherTypeData.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 transition-colors"
                    onClick={() => handleVoucherTypeDrillDown(item.name)}
                  >
                    <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: item.fill }}></span>
                    <span className="text-[9px] text-gray-500 truncate" title={item.name}>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top 10 Items Pie Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Top 10 Items Purchased</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="top-10-items-purchased" />
              </div>
            </div>
            <div className="flex-1 p-2 flex overflow-hidden">
              <div className="flex-[1.5] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={top10ItemsPurchased}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={40}
                      dataKey="value"
                      labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                      label={renderCustomLabel}
                      onClick={(data) => {
                        if (data && data.name) handleItemNameDrillDown(data.name);
                      }}
                    >
                      {top10ItemsPurchased.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.fill}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleItemNameDrillDown(entry.name)}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 flex flex-col gap-1 ml-1 overflow-y-auto pr-1 custom-scrollbar">
                <h4 className="text-[10px] font-bold text-gray-900 mb-1">Item Name</h4>
                {top10ItemsPurchased.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 transition-colors"
                    onClick={() => handleItemNameDrillDown(item.name)}
                  >
                    <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: item.fill }}></span>
                    <span className="text-[9px] text-gray-500 truncate" title={item.name}>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Count by Supplier Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Count of item Name by Supplier Name</h3>
              <div className="flex items-center gap-2">
                <Select value={sortOrder['count-by-supplier'] || 'low-high'} onValueChange={(v) => setSortOrder(p => ({ ...p, 'count-by-supplier': v }))}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="count-by-supplier" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-2 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Count
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-17px' }}>
                {(() => {
                  let data = countBySupplier || [];
                  const currentSortOrder = sortOrder['count-by-supplier'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.count) || 0;
                      const bValue = Number(b.count) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const maxLabelLength = Math.max(...data.map(item => ((item.name) || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-count-by-supplier-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -5, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} tickFormatter={(value) => value} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="count"
                            fill="#ef4444"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (data && data.supplier_name) {
                                handleSupplierNameDrillDown(data.supplier_name);
                                console.log('Dashboard: Clicked on supplier bar, supplier:', data.supplier_name);
                              }
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Supplier Name
              </div>
            </div>
          </div>

          {/* Spare Uptime Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Spare Uptime by 2 Item Name</h3>
              <div className="flex items-center gap-2">
                <Select value={sortOrder['spare-uptime-by-item'] || 'low-high'} onValueChange={(v) => setSortOrder(p => ({ ...p, 'spare-uptime-by-item': v }))}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="spare-uptime-by-item" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-2 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Uptime
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-17px' }}>
                {(() => {
                  let data = spareUptimeByItem || [];
                  const currentSortOrder = sortOrder['spare-uptime-by-item'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.uptime) || 0;
                      const bValue = Number(b.uptime) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const maxLabelLength = Math.max(...data.map(item => ((item.name) || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-spare-uptime-by-item-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -5, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} tickFormatter={(value) => value} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v / 1000}K`} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="uptime"
                            fill="#3b82f6"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (data && data.name) {
                                handleItemNameDrillDown(data.name);
                                console.log('Dashboard: Clicked on spare uptime item:', data.name);
                              }
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Item Name
              </div>
            </div>
          </div>

          {/* Cost/Hr Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Cost/Hr by Item Name </h3>
              <div className="flex items-center gap-2">
                <Select value={sortOrder['cost-per-hr-by-item'] || 'low-high'} onValueChange={(v) => setSortOrder(p => ({ ...p, 'cost-per-hr-by-item': v }))}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="cost-per-hr-by-item" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-2 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Cost/Hr
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-17px' }}>
                {(() => {
                  let data = costPerHrByItem || [];
                  const currentSortOrder = sortOrder['cost-per-hr-by-item'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.costPerHr) || 0;
                      const bValue = Number(b.costPerHr) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const maxLabelLength = Math.max(...data.map(item => ((item.name) || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-cost-per-hr-by-item-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -5, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} tickFormatter={(value) => value} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="costPerHr"
                            fill="#ec4899"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            onClick={(data) => {
                              if (data && data.name) {
                                handleItemNameDrillDown(data.name);
                                console.log('Clicked on cost/hr item:', data.name);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Item Name
              </div>
            </div>
          </div>

          {/* Sum of Amount Line Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Sum of Amt by Yr, Quarter, Month and Day</h3>
              <div className="flex items-center gap-2">
                <Select value={sumOfAmountResolution} onValueChange={setSumOfAmountResolution}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Res" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="sum-of-amount-by-date" />
              </div>
            </div>
            <div className="flex-1 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sumOfAmountByDate.slice(-30)} margin={{ top: 5, right: 20, left: 25, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `${v}M`}
                    label={{ value: 'Sum of Amount', angle: -90, offset: -20, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="forecast" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Count of Items Line Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Count of Items by Yr, Quarter, Month and Day</h3>
              <div className="flex items-center gap-2">
                <Select value={countOfItemsResolution} onValueChange={setCountOfItemsResolution}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Res" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="count-of-item-name-by-date" />
              </div>
            </div>
            <div className="flex-1 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={countOfItemNameByDate.slice(-30)} margin={{ top: 5, right: 20, left: 25, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `${v}M`}
                    label={{ value: 'No of Items', angle: -90, offset: -20, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="forecast" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>
      </div>

      {/* Chart Modal for Maximized View */}
      {
        maximizedChartId && (
          <ChartModal
            isOpen={!!maximizedChartId}
            onClose={handleCloseModal}
            title={chartConfigs[maximizedChartId]?.title || 'Chart'}
            headerActions={renderHeaderActions(maximizedChartId)}
            showEquipmentFilter={!['sum-of-amount-by-date', 'count-of-item-name-by-date'].includes(maximizedChartId)}
            equipmentList={getEquipmentListForChart(maximizedChartId)}
            selectedEquipment={modalEquipmentFilter}
            onEquipmentChange={setModalEquipmentFilter}
            filterLabel={getFilterLabelForChart(maximizedChartId)}
            showSortFilter={chartConfigs[maximizedChartId]?.chartType === 'pie'}
            sortOrder={modalSortOrder}
            onSortChange={setModalSortOrder}
          >
            {renderMaximizedChart(maximizedChartId)}
          </ChartModal>
        )
      }
    </div >
  );
};

export default Spares;

